import axios from "axios";
import { getBackendUrl } from "../../utils/backendConfig";

const API_BASE = getBackendUrl() + "/api/v1";

const adminApi = axios.create({ baseURL: API_BASE });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin-token");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?._skipAuthRedirect
    ) {
      localStorage.removeItem("admin-token");
      const from = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/admin/login?from=${from}`;
    }
    return Promise.reject(error);
  }
);

export async function adminLogin(email: string, password: string): Promise<string> {
  const res = await axios.post(`${getBackendUrl()}/api/v1/auth/login`, { email, password });
  return res.data.access_token;
}

export function authCheck() {
  return adminApi.get("/admin/shops", {
    params: { size: 1 },
    _skipAuthRedirect: true,
  } as any);
}

export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  shop_id: number;
  roles: string[];
}

export function getMe() {
  return adminApi.get<CurrentUser>("/auth/me").then((r) => r.data);
}

// ─── Owner admin (multi-shop dashboard) ─────────────────────────────────────

export type Period = "1d" | "7d" | "30d";
export type SignedStatus = "any" | "signed" | "unsigned" | "invalid";
// Which date drives buckets/filters: "complete" (COALESCE complete→intake,
// default) or "intake". Header toggle, shared across admin pages.
export type DateBasis = "complete" | "intake";

export interface ShopBrief {
  id: number;
  name: string;
}

export interface ScopeResponse {
  accessible_shop_ids: number[] | null; // null = all shops (DATIUM)
  shops: ShopBrief[];
}

export interface DashboardStats {
  period: Period;
  legal_doc_total: number;
  legal_doc_signed: number;  // ESTIMATE-track alias (back-compat)
  legal_doc_invalid: number;
  legal_doc_signed_estimate?: number;
  legal_doc_signed_invoice?: number;
  revenue_cents: number;
  revenue_cents_prev: number;
  jobs: number;
  revenue_trend: { day: string; revenue_cents: number }[];
  top_team: { user_id: number; name: string; jobs: number; revenue_cents: number }[];
  recent_signed: {
    conversation_id: number;
    shop_id: number;
    customer_name: string;
    vehicle: string;
    total_cents: number;
    signature_method: string | null;
    signed_status: string;
    updated_at: string;
  }[];
}

export interface JobRow {
  conversation_id: number;
  shop_id: number;
  customer_name: string;
  vehicle: string;
  total_cents: number;
  signed_status: SignedStatus;
  // Legal-safe badge: true = every selected item covered by a real legal doc,
  // false = at least one uncovered, null = nothing to authorize. Tek-side
  // authorizations do NOT count (mirrors the shop app's green/amber shield).
  quote_legal_safe: boolean | null;
  // Two-track split (estimate left / invoice right on the shield pill).
  quote_legal_safe_estimate?: boolean | null;
  quote_legal_safe_invoice?: boolean | null;
  signature_method: string | null;
  updated_at: string;
  // Real shop timeline; the list orders/displays by complete_date ?? intake_date.
  intake_date: string | null;
  complete_date: string | null;
  charger_names: string[];
  items_quoted: number;
  items_approved: number;
  items_declined: number;
  cents_quoted: number;
  cents_approved: number;
  cents_declined: number;
  // Report-engagement / review KPIs (folded in from the former Reviews tab).
  report_sent: boolean;
  opened: boolean;
  cta_count: number;
  review_platforms: string[];
  reviews_count: number;
  avg_rating: number | null;
  latest_rating: number | null;
  latest_comment: string | null;
}

export interface JobsResponse {
  rows: JobRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface TeamMember {
  user_id: number;
  name: string;
  shop_ids: number[];
  jobs: number;
  revenue_cents: number;
  last_active_at: string | null;
  items_quoted?: number;
  items_approved?: number;
  items_declined?: number;
  cents_quoted?: number;
  cents_approved?: number;
  cents_declined?: number;
  signed_jobs?: number;
}

export interface LegalDocRow {
  quote_id: number;
  conversation_id: number;
  method: string;
  signed_at: string | null;
  authorizer_name: string | null;
  signature_evidence_blob: string | null;
  file_url?: string | null;
  pdf_url?: string | null;
  hmac_valid: boolean | null;
  kind: "approval" | "approval_event" | "tek_authorization";
  method_detail?: string | null;
  ro_total_cents?: number | null;
  ip?: string | null;
  signature_status?: string | null;
  is_safe?: boolean | null;
  tek_repair_order_id?: number | null;
}

export interface ChatResponse {
  query_id: string | null;
  filters: Record<string, unknown>;
  explanation: string;
  rows: Record<string, unknown>[];
  truncated: boolean;
}

function shopIdsParam(ids: number[]): string | undefined {
  return ids.length ? ids.join(",") : undefined;
}

// Serialize the multi-select tech filter to the comma-separated query param
// the backend's `charger_user_ids` expects (mirrors shopIdsParam).
function chargerIdsParam(ids: number[] | undefined): string | undefined {
  return ids && ids.length ? ids.join(",") : undefined;
}

// One charger's metrics for a single day — feeds the Per Charger graph (a trend
// line per charger) and calendar (the leading charger per day).
export interface ChargerDayRow {
  user_id: number | null;
  name: string;
  day: string;
  jobs: number;
  signed_count: number;  // ESTIMATE-track alias (back-compat)
  signed_count_estimate?: number;
  signed_count_invoice?: number;
  revenue_cents: number;
  items_approved: number;
  items_declined: number;
  cents_approved: number;
  cents_declined: number;
  // Report-engagement / review counters (per charger, per day).
  reports_sent: number;
  opened_convs: number;
  review_click_convs: number;
  cta_convs: number;
  reviews_count: number;
  rating_sum: number;
}

export const ownerApi = {
  scope: () => adminApi.get<ScopeResponse>("/owner/scope").then((r) => r.data),

  dashboard: (
    shopIds: number[],
    period: Period = "30d",
    opts: { q?: string; from?: string; to?: string; signed?: SignedStatus; charger_user_ids?: number[]; date_basis?: "complete" | "intake" } = {},
  ) =>
    adminApi
      .get<DashboardStats>("/owner/dashboard", {
        params: {
          shop_ids: shopIdsParam(shopIds),
          period,
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed && opts.signed !== "any" ? opts.signed : undefined,
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
          date_basis: opts.date_basis || undefined,
        },
      })
      .then((r) => r.data),

  jobs: (
    shopIds: number[],
    opts: {
      q?: string;
      from?: string;
      to?: string;
      signed?: SignedStatus;
      charger_user_ids?: number[];
      sort?: string;
      page?: number;
      page_size?: number;
    } = {}
  ) =>
    adminApi
      .get<JobsResponse>("/owner/jobs", {
        params: {
          shop_ids: shopIdsParam(shopIds),
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed || "any",
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
          sort: opts.sort || undefined,
          page: opts.page || 1,
          page_size: opts.page_size || 50,
        },
      })
      .then((r) => r.data),

  jobsStats: (
    shopIds: number[],
    opts: {
      q?: string;
      from?: string;
      to?: string;
      signed?: SignedStatus;
      charger_user_ids?: number[];
    } = {}
  ) =>
    adminApi
      .get<{
        total_jobs: number;
        signed_jobs: number;
        unsigned_jobs: number;
        revenue_cents: number;
        revenue_cents_prev: number;
        items_quoted: number;
        items_approved: number;
        items_declined: number;
        cents_quoted: number;
        cents_approved: number;
        cents_declined: number;
        revenue_trend: {
          day: string;
          jobs: number;
          signed_count: number;
          revenue_cents: number;
          items_quoted: number;
          items_approved: number;
          items_declined: number;
          cents_quoted: number;
          cents_approved: number;
          cents_declined: number;
          reports_sent: number;
          opened: number;
          review_click: number;
          cta: number;
          reviews_count: number;
          rating_sum: number;
        }[];
      }>("/owner/jobs/stats", {
        params: {
          shop_ids: shopIdsParam(shopIds),
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed || "any",
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
        },
      })
      .then((r) => r.data),

  jobsByCharger: (
    shopIds: number[],
    opts: {
      q?: string;
      from?: string;
      to?: string;
      signed?: SignedStatus;
      charger_user_ids?: number[];
    } = {}
  ) =>
    adminApi
      .get<{
        user_id: number | null;
        name: string;
        shop_ids: number[];
        jobs: number;
        signed_jobs: number;
        revenue_cents: number;
        items_quoted: number;
        items_approved: number;
        items_declined: number;
        cents_quoted: number;
        cents_approved: number;
        cents_declined: number;
        reports_sent: number;
        opened_convs: number;
        review_click_convs: number;
        cta_convs: number;
        reviews_count: number;
        avg_rating: number | null;
        last_active_at: string | null;
      }[]>("/owner/jobs/by-charger", {
        params: {
          shop_ids: shopIdsParam(shopIds),
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed || "any",
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
        },
      })
      .then((r) => r.data),

  jobsByChargerDaily: (
    shopIds: number[],
    opts: {
      q?: string;
      from?: string;
      to?: string;
      signed?: SignedStatus;
      charger_user_ids?: number[];
      date_basis?: "complete" | "intake";
    } = {}
  ) =>
    adminApi
      .get<ChargerDayRow[]>("/owner/jobs/by-charger-daily", {
        params: {
          shop_ids: shopIdsParam(shopIds),
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed || "any",
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
          date_basis: opts.date_basis || undefined,
        },
      })
      .then((r) => r.data),

  jobsCalendar: (
    shopIds: number[],
    opts: {
      q?: string;
      from?: string;
      to?: string;
      signed?: SignedStatus;
      charger_user_ids?: number[];
    } = {}
  ) =>
    adminApi
      .get<{
        day: string;
        jobs: number;
        signed_count: number;
        revenue_cents: number;
        items_quoted: number;
        items_approved: number;
        items_declined: number;
        cents_quoted: number;
        cents_approved: number;
        cents_declined: number;
        reports_sent: number;
        opened: number;
        review_click: number;
        cta: number;
        reviews_count: number;
        rating_sum: number;
      }[]>("/owner/jobs/calendar", {
        params: {
          shop_ids: shopIdsParam(shopIds),
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed || "any",
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
        },
      })
      .then((r) => r.data),

  team: (shopIds: number[], period: Period = "30d", date_basis?: DateBasis) =>
    adminApi
      .get<{ rows: TeamMember[] }>("/owner/team", {
        params: { shop_ids: shopIdsParam(shopIds), period, date_basis: date_basis === "intake" ? "intake" : undefined },
      })
      .then((r) => r.data),

  legalDocs: (conversationId: number) =>
    adminApi
      .get<{ rows: LegalDocRow[] }>("/owner/legal-docs", { params: { conversation_id: conversationId } })
      .then((r) => r.data),

  conversation: (conversationId: number) =>
    adminApi
      .get<{
        summary: {
          conversation_id: number;
          shop_id: number;
          shop_name: string;
          updated_at: string;
          lead_source: string | null;
          customer_name: string;
          vehicle: string;
          charger_names: string[];
          total_cents: number;
          items_quoted: number;
          items_approved: number;
          items_declined: number;
          cents_quoted: number;
          cents_approved: number;
          cents_declined: number;
          signed_status: string;
          signature_method: string | null;
          hmac_valid: boolean | null;
          quote_items: {
            source_id: string | null;
            label: string;
            amount_cents: number;
            item_status: "unauthorized" | "selected" | "approved" | "declined" | null;
          }[];
        };
        legal_docs: LegalDocRow[];
      }>(`/owner/conversations/${conversationId}`)
      .then((r) => r.data),

  chat: (prompt: string, shopIds: number[]) =>
    adminApi.post<ChatResponse>("/owner/chat", { prompt, shop_ids: shopIds }).then((r) => r.data),

  dashboardShops: (
    shopIds: number[],
    period: Period = "30d",
    opts: { q?: string; from?: string; to?: string; signed?: SignedStatus; charger_user_ids?: number[]; date_basis?: "complete" | "intake" } = {},
  ) =>
    adminApi.get<{
      shop_id: number;
      shop_name: string;
      jobs: number;
      signed_jobs: number;
      revenue_cents: number;
      sparkline: { day: string; revenue_cents: number }[];
    }[]>("/owner/dashboard/shops", {
      params: {
        shop_ids: shopIdsParam(shopIds),
        period,
        q: opts.q || undefined,
        from: opts.from || undefined,
        to: opts.to || undefined,
        signed: opts.signed && opts.signed !== "any" ? opts.signed : undefined,
        charger_user_ids: chargerIdsParam(opts.charger_user_ids),
        date_basis: opts.date_basis || undefined,
      },
    }).then((r) => r.data),

  dashboardActivity: (shopIds: number[], limit = 25) =>
    adminApi.get<{
      conversation_id: number;
      shop_id: number;
      shop_name: string;
      at: string;
      method: string;
      kind: string;
      authorizer: string | null;
      customer_name: string;
      vehicle: string;
    }[]>("/owner/dashboard/activity", { params: { shop_ids: shopIdsParam(shopIds), limit } }).then((r) => r.data),

  dashboardFunnel: (shopIds: number[], period: Period = "30d") =>
    adminApi.get<{ label: string; count: number }[]>(
      "/owner/dashboard/funnel",
      { params: { shop_ids: shopIdsParam(shopIds), period } }
    ).then((r) => r.data),

  insightsTurnaround: (
    shopIds: number[],
    period: Period = "30d",
    opts: { q?: string; from?: string; to?: string; signed?: SignedStatus; charger_user_ids?: number[]; date_basis?: "complete" | "intake" } = {},
  ) =>
    adminApi
      .get<{
        by_shop: { shop_id: number; shop_name: string; decided_jobs: number; median_hours: number | null }[];
        by_charger: { user_id: number | null; name: string; decided_jobs: number; median_hours: number | null }[];
      }>("/owner/insights/turnaround", {
        params: {
          shop_ids: shopIdsParam(shopIds), period,
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed && opts.signed !== "any" ? opts.signed : undefined,
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
          date_basis: opts.date_basis || undefined,
        },
      })
      .then((r) => r.data),

  insightsTopItems: (shopIds: number[], period: Period = "30d", limit: number = 8) =>
    adminApi
      .get<{
        top_approved: { label: string; count: number; total_cents: number }[];
        top_declined: { label: string; count: number; total_cents: number }[];
      }>("/owner/insights/top-items", {
        params: { shop_ids: shopIdsParam(shopIds), period, limit },
      })
      .then((r) => r.data),

  insightsBreakdown: (
    shopIds: number[],
    period: Period = "30d",
    compare: boolean = false,
    opts: { q?: string; from?: string; to?: string; signed?: SignedStatus; charger_user_ids?: number[]; date_basis?: "complete" | "intake" } = {},
  ) =>
    adminApi
      .get<{
        by_shop: ({ shop_id: number; shop_name: string; jobs: number; signed_jobs: number; signed_jobs_invoice?: number; items_quoted: number; items_approved: number; items_declined: number; revenue_cents: number; prior?: { jobs: number; signed_jobs: number; signed_jobs_invoice?: number; items_quoted: number; items_approved: number; items_declined: number; revenue_cents: number } | null })[];
        by_charger: ({ user_id: number | null; name: string; jobs: number; signed_jobs: number; signed_jobs_invoice?: number; items_quoted: number; items_approved: number; items_declined: number; revenue_cents: number; prior?: { jobs: number; signed_jobs: number; signed_jobs_invoice?: number; items_quoted: number; items_approved: number; items_declined: number; revenue_cents: number } | null })[];
        by_day: { day: string; jobs: number; signed_jobs: number; signed_jobs_invoice?: number; items_quoted: number; items_approved: number; items_declined: number; revenue_cents: number }[];
        by_make: { make: string; jobs: number; signed_jobs: number; signed_jobs_invoice?: number; items_quoted: number; items_approved: number; items_declined: number; revenue_cents: number }[];
      }>("/owner/insights/breakdown", {
        params: {
          shop_ids: shopIdsParam(shopIds), period, compare,
          q: opts.q || undefined,
          from: opts.from || undefined,
          to: opts.to || undefined,
          signed: opts.signed && opts.signed !== "any" ? opts.signed : undefined,
          charger_user_ids: chargerIdsParam(opts.charger_user_ids),
          date_basis: opts.date_basis || undefined,
        },
      })
      .then((r) => r.data),
};

export interface AdminLegalDoc {
  id: string;
  source: "haist" | "tek";
  doc_type?: "estimate" | "invoice";
  shop_id: number;
  shop_name: string | null;
  conversation_id: number;
  ro_number: number | null;
  method: string | null;
  signed: boolean;
  signature_detected: boolean;
  has_integrity: boolean;
  safe: boolean | null;
  flag: "unsigned_shell" | "drift" | "no_integrity" | "ok";
  authorizer_name: string | null;
  signed_at: string | null;
  total: number | null;
  file_url: string | null;
}

export const legalDocsApi = {
  list: (flag?: string, docType?: string) =>
    adminApi
      .get<{ documents: AdminLegalDoc[]; total: number; counts: Record<string, number> }>(
        "/admin/legal-documents",
        { params: { flag: flag || undefined, doc_type: docType || undefined, limit: 500 } },
      )
      .then((r) => r.data),
};

export default adminApi;
